import { styled } from "goober";
import { useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import type { StreamMessage } from "../../hooks/useStreamingText";
import { pluralize } from "../../lib/inflection";
import { playClick } from "../../lib/sounds";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import { WavyLine } from "../WavyLine";
import type { ChatTool } from "./ChatTool";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandedContentHover,
  ExpandedTimestamp,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";
import { ToolUse } from "./ToolUse";
import { TypewriterText } from "./TypewriterText";

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GroupHeader = styled("div")`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 4px;
  padding: 6px 10px 6px 8px;
  border-radius: 9999px;
  margin-top: 0;
  width: fit-content;
  &[data-interactive="true"] {
    cursor: pointer;
    &:hover {
      background-color: var(--alpha4);
      ${DefaultIcon} {
        opacity: 0;
        transform: rotate(90deg) scale(0.5);
      }
      ${HoverIcon} {
        opacity: 1;
        transform: rotate(0deg) scale(1);
      }
    }
  }
`;

const GroupHeaderInner = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ToolsContainer = styled("div")`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

const GroupExpandInner = styled("div")`
  overflow: hidden;
  min-height: 0;
  content-visibility: hidden;
  &[data-expanded="true"] {
    overflow-y: visible;
    content-visibility: visible;
  }
`;

type Props = {
  entries: ChatEntryWithSubTables[];
  reasoning?: ChatEntryWithSubTables;
  collapsed: boolean;
  toggleCollapsed: () => void;
  expandedToolUse: string | null;
  setExpandedToolUse: (id: string | null) => void;
  tools: ReadonlyMap<string, ChatTool>;
  isTrailing?: boolean;
  onProvideResult?: (
    entryId: string,
    output: unknown,
    isError: boolean
  ) => void;
  streamingEntries?: ReadonlyMap<string, StreamMessage>;
};

export function ToolUseGroup({
  entries,
  reasoning,
  collapsed,
  toggleCollapsed,
  expandedToolUse,
  setExpandedToolUse,
  tools,
  isTrailing,
  onProvideResult,
  streamingEntries,
}: Props) {
  const [expandedReasoning, setExpandedReasoning] = useState(false);

  const isReasoningStreaming =
    !!reasoning?.assistantReasoning && !reasoning.assistantReasoning.signature;
  const isReasoningSseActive =
    isReasoningStreaming &&
    streamingEntries?.get(reasoning?.id ?? "")?.status === "streaming";

  const reasoningHeaderText = isReasoningStreaming
    ? "Thinking…"
    : reasoning?.assistantReasoning?.durationMs
      ? (() => {
          const s = Math.max(
            1,
            Math.round(reasoning.assistantReasoning.durationMs / 1000)
          );
          return `Thought for ${s} ${pluralize("second", s)}`;
        })()
      : "Thought";

  const reasoningText =
    (reasoning?.id && streamingEntries?.get(reasoning.id)?.text) ||
    reasoning?.assistantReasoning?.text;

  // Reasoning block (rendered separately if present)
  const reasoningBlock = reasoning?.assistantReasoning && (
    <CollapsibleHeader onPress={() => setExpandedReasoning((prev) => !prev)}>
      <CollapsibleHeaderInner>
        {isReasoningStreaming ? (
          <WavyLine size={12} color="var(--text-secondary)" opacity={1} />
        ) : (
          <ToggleIconContainer aria-hidden>
            <DefaultIcon>
              <Icon name="brain" size={12} color="var(--text-secondary)" />
            </DefaultIcon>
            <HoverIcon>
              <Icon
                name={expandedReasoning ? "minus" : "plus"}
                size={12}
                color="var(--text-secondary)"
              />
            </HoverIcon>
          </ToggleIconContainer>
        )}
        <Text variant="text-small" color="var(--text-secondary)">
          {reasoningHeaderText}
        </Text>
      </CollapsibleHeaderInner>
    </CollapsibleHeader>
  );

  const reasoningContent = reasoning?.assistantReasoning && reasoningText && (
    <ExpandWrapper data-expanded={expandedReasoning}>
      <ExpandInner data-expanded={expandedReasoning}>
        <ExpandedContentHover>
          <ExpandedContent>
            <TypewriterText
              text={reasoningText}
              streaming={!!isReasoningSseActive}
              plain
              variant="text-small"
              color="var(--text-secondary)"
            />
          </ExpandedContent>
          <ExpandedTimestamp
            createdAt={reasoning.createdAt}
            durationMs={reasoning.assistantReasoning.durationMs}
          />
        </ExpandedContentHover>
      </ExpandInner>
    </ExpandWrapper>
  );

  // Reasoning-only group (no tools)
  if (entries.length === 0) {
    if (!reasoning?.assistantReasoning) return null;
    return (
      <Container>
        {reasoningBlock}
        {reasoningContent}
      </Container>
    );
  }

  // Tools group (with optional separate reasoning block above)
  return (
    <Container>
      {reasoningBlock}
      {reasoningContent}
      <GroupHeader
        data-interactive={!isTrailing}
        onMouseDown={
          isTrailing
            ? undefined
            : (e) => {
                if (e.button !== 0) return;
                playClick();
                toggleCollapsed();
              }
        }
      >
        <GroupHeaderInner>
          {isTrailing ? (
            <WavyLine size={12} color="var(--text-secondary)" opacity={1} />
          ) : (
            <ToggleIconContainer aria-hidden>
              <DefaultIcon>
                <Icon name="toolbox" size={12} color="var(--text-secondary)" />
              </DefaultIcon>
              <HoverIcon>
                <Icon
                  name={collapsed ? "plus" : "minus"}
                  size={12}
                  color="var(--text-secondary)"
                />
              </HoverIcon>
            </ToggleIconContainer>
          )}
          <Text variant="text-small" color="var(--text-secondary)">
            {isTrailing
              ? "Using tools…"
              : `${entries.length} ${pluralize("tool", entries.length)} used`}
          </Text>
        </GroupHeaderInner>
      </GroupHeader>
      <ExpandWrapper data-expanded={!collapsed}>
        <GroupExpandInner data-expanded={!collapsed}>
          <ToolsContainer>
            {entries.map((entry, index) => {
              const isLast = index === entries.length - 1;
              const toolAfter = !isLast;
              const interrupted =
                !isTrailing && entry.toolCall!.completedAt == null;

              return (
                <ToolUse
                  key={entry.id}
                  toolUse={{
                    id: entry.id,
                    toolName: entry.toolCall!.toolName,
                    input:
                      streamingEntries?.get(entry.id)?.toolInput ??
                      entry.toolCall!.input,
                    output: entry.toolCall!.output,
                    isError: entry.toolCall!.isError,
                  }}
                  result={
                    entry.toolCall!.completedAt != null
                      ? {
                          id: entry.id,
                          output: entry.toolCall!.output,
                          isError: entry.toolCall!.isError ?? false,
                        }
                      : null
                  }
                  expanded={expandedToolUse === entry.id}
                  toggleExpanded={() =>
                    setExpandedToolUse(
                      expandedToolUse === entry.id ? null : entry.id
                    )
                  }
                  toolAfter={toolAfter}
                  tool={tools.get(entry.toolCall!.toolName)}
                  createdAt={entry.createdAt}
                  completedAt={entry.toolCall!.completedAt}
                  interrupted={interrupted}
                  onProvideResult={onProvideResult}
                />
              );
            })}
          </ToolsContainer>
        </GroupExpandInner>
      </ExpandWrapper>
    </Container>
  );
}
